<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ExternalLink } from '@lucide/vue'
import { Badge, Button, Modal, Spinner, Table } from '@drama/ui'

import { getNovelPack, getPublishedNovelVersion, uploadNovelOpeningCgAsset } from '../../../api/novel/packs.js'
import { visualGameSiteUrl } from '../../../lib/visual-game-preview-url.js'
import { useNovelPageText } from '../novel-page-text.js'
import PackRelease from './PackRelease.vue'
import { castOf, openingLines } from './pack-content.js'
import { STATUS_VARIANT, VALIDATION_VARIANT, isListedInvalid, packStatusKey } from './pack-status.js'

const props = defineProps({
  // 要预览的作品 id；空串表示弹层关着。
  packId: { type: String, default: '' },
})
const emit = defineEmits(['close'])

const { t } = useI18n()
const { apiError, dateTime } = useNovelPageText()
const tp = (key, params) => t(`visualGameAdmin.novelPacks.${key}`, params ?? {})

const loading = ref(false)
const error = ref(null)
const detail = ref(null)
const frozen = ref(null)
const cgKind = ref('video')
const cgFile = ref(null)
const cgBusy = ref(false)
const cgError = ref(null)
const cgResult = ref(null)

const pack = computed(() => detail.value?.pack ?? null)
const cast = computed(() => (detail.value ? castOf(detail.value.content) : []))
const opening = computed(() => (detail.value ? openingLines(detail.value.content) : []))
const versionColumns = computed(() => [
  { key: 'version', label: tp('detail.versionColumns.version'), width: '100px', mono: true },
  { key: 'note', label: tp('detail.versionColumns.note'), width: '260px', wrap: true },
  { key: 'validation_status', label: tp('detail.versionColumns.validation'), width: '120px' },
  { key: 'published_at', label: tp('detail.versionColumns.publishedAt'), width: '180px' },
  { key: 'package_hash', label: tp('detail.versionColumns.hash'), width: '300px', mono: true },
])
// 创作台编辑器与玩家详情页的地址由后台域名换算，换算不了（后台不在 admin- 域名下）时在链接位置写明原因。
// 玩家详情页只对已上架作品开放，其余状态不给这条链接。
const links = computed(() => {
  if (!pack.value) return null
  try {
    return {
      studio: pack.value.status === 'deleted' ? '' : visualGameSiteUrl(window.location.origin, 'studio', `/novels/${encodeURIComponent(pack.value.id)}/editor`).href,
      player: pack.value.status === 'published' ? visualGameSiteUrl(window.location.origin, 'player', `/novel/${encodeURIComponent(pack.value.slug)}`).href : '',
      error: '',
    }
  } catch (linkError) {
    return { studio: '', player: '', error: linkError.message }
  }
})

watch(() => props.packId, (packId) => {
  frozen.value = null
  cgFile.value = null
  cgError.value = null
  cgResult.value = null
  if (packId) load()
}, { immediate: true })
watch(cgKind, () => { cgFile.value = null })

async function loadFrozen() {
  const packId = props.packId
  cgError.value = null
  try {
    const result = await getPublishedNovelVersion(packId)
    if (props.packId === packId) frozen.value = result
  } catch (failure) {
    if (props.packId === packId) cgError.value = apiError(failure)
  }
}

async function uploadCg() {
  if (!cgFile.value || cgBusy.value) return
  const packId = props.packId
  cgBusy.value = true
  cgError.value = null
  cgResult.value = null
  try {
    const result = await uploadNovelOpeningCgAsset(packId, cgKind.value, cgFile.value)
    if (props.packId === packId) cgResult.value = result
  } catch (failure) {
    if (props.packId === packId) cgError.value = apiError(failure)
  } finally {
    cgBusy.value = false
  }
}

/** 只认最后一次打开的作品：弹层关掉或换了作品后，旧请求的结果丢弃。 */
async function load() {
  const packId = props.packId
  detail.value = null
  error.value = null
  loading.value = true
  try {
    const next = await getNovelPack(packId)
    if (props.packId === packId) detail.value = next
  } catch (requestError) {
    if (props.packId === packId) error.value = apiError(requestError)
  } finally {
    if (props.packId === packId) loading.value = false
  }
}

function onOpenChange(open) {
  if (!open) emit('close')
}
</script>

<template>
  <Modal :open="packId !== ''" :title="tp('detail.title')" width="800px" @update:open="onOpenChange">
    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error" :title="error.title">{{ error.text }}</p>
      <Button variant="secondary" data-testid="novel-pack-detail-retry" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <div v-else-if="pack" class="pp" data-testid="novel-pack-detail">
      <img v-if="pack.cover_url" class="pp__cover" :src="pack.cover_url" :alt="tp('detail.cover')" />
      <header class="pp__head">
        <div class="pp__name">
          <h3>{{ pack.title }}</h3>
          <span class="pp__muted pp__mono">{{ pack.slug }}</span>
        </div>
        <div class="pp__links">
          <template v-if="!links.error">
            <a v-if="links.studio" class="pp__link" :href="links.studio" target="_blank" rel="noopener" :title="tp('detail.openStudioHint')" data-testid="novel-pack-open-studio">
              <ExternalLink :size="14" aria-hidden="true" /> {{ tp('detail.openStudio') }}
            </a>
            <a v-if="links.player" class="pp__link" :href="links.player" target="_blank" rel="noopener" data-testid="novel-pack-open-player">
              <ExternalLink :size="14" aria-hidden="true" /> {{ tp('detail.openPlayer') }}
            </a>
          </template>
          <span v-else class="pp__muted">{{ links.error }}</span>
        </div>
      </header>
      <p v-if="isListedInvalid(pack)" class="pp__risk">{{ tp('listedInvalidHint') }}</p>

      <section class="pp__section">
        <h4>{{ tp('detail.teaser') }}</h4>
        <p class="pp__text">{{ detail.teaser || tp('detail.empty') }}</p>
      </section>

      <section class="pp__section">
        <h4>{{ tp('detail.characters') }} · {{ cast.length }}</h4>
        <ul v-if="cast.length" class="pp__cast">
          <li v-for="person in cast" :key="person.key" class="pp__person">
            <img v-if="person.image" class="pp__avatar" :src="person.image" :alt="person.name" loading="lazy" />
            <span v-else class="pp__avatar" aria-hidden="true">{{ person.initial }}</span>
            <div class="pp__person-body">
              <p class="pp__person-name">
                {{ person.name || '—' }}
                <Badge v-if="person.is_player" size="sm" variant="info">{{ tp('detail.playerRole') }}</Badge>
              </p>
              <p class="pp__text pp__muted">{{ person.public_profile || tp('detail.empty') }}</p>
            </div>
          </li>
        </ul>
        <p v-else class="pp__muted">{{ tp('detail.empty') }}</p>
      </section>

      <section class="pp__section">
        <h4>{{ tp('detail.opening') }}</h4>
        <div v-if="opening.length" class="pp__opening">
          <p v-for="line in opening" :key="line.key" class="pp__text">
            <span v-if="line.speaker" class="pp__speaker">{{ line.speaker }}</span>{{ line.text }}
          </p>
        </div>
        <p v-else class="pp__muted">{{ tp('detail.empty') }}</p>
      </section>

      <dl class="pp__facts">
        <div><dt>{{ tp('detail.owner') }}</dt><dd>{{ pack.owner_email || '—' }}</dd></div>
        <div><dt>{{ tp('detail.language') }}</dt><dd>{{ pack.language || '—' }}</dd></div>
        <div><dt>{{ tp('detail.status') }}</dt><dd><Badge size="sm" :variant="STATUS_VARIANT[pack.status]">{{ t(packStatusKey(pack.status)) }}</Badge></dd></div>
        <div><dt>{{ tp('detail.validation') }}</dt><dd><Badge size="sm" :variant="VALIDATION_VARIANT[pack.validation_status]">{{ tp(`validation.${pack.validation_status}`) }}</Badge></dd></div>
        <div><dt>{{ tp('detail.currentVersion') }}</dt><dd class="pp__mono">{{ pack.latest_version || '—' }}</dd></div>
        <div><dt>{{ tp('detail.listedAt') }}</dt><dd>{{ pack.listed_at ? dateTime(pack.listed_at) : '—' }}</dd></div>
        <div><dt>{{ tp('detail.contentHash') }}</dt><dd class="pp__mono" :title="pack.content_hash">{{ pack.content_hash.slice(0, 12) }}</dd></div>
        <div><dt>{{ tp('detail.createdAt') }}</dt><dd>{{ dateTime(pack.created_at) }}</dd></div>
        <div><dt>{{ tp('detail.updatedAt') }}</dt><dd>{{ dateTime(pack.updated_at) }}</dd></div>
      </dl>

      <section class="pp__section">
        <h4>{{ tp('detail.versions') }}</h4>
        <p v-if="detail.versions.length === 0" class="pp__muted">{{ tp('detail.noVersions') }}</p>
        <Table v-else :columns="versionColumns" :rows="detail.versions">
          <template #note="{ row }">{{ row.note || '—' }}</template>
          <template #validation_status="{ row }"><Badge size="sm" :variant="VALIDATION_VARIANT[row.validation_status]">{{ tp(`validation.${row.validation_status}`) }}</Badge></template>
          <template #published_at="{ row }">{{ dateTime(row.published_at) }}</template>
        </Table>
      </section>

      <section v-if="pack.status === 'published'" class="pp__section">
        <h4>{{ tp('detail.openingCg.title') }}</h4>
        <Button variant="secondary" size="sm" data-testid="novel-published-version-load" @click="loadFrozen">{{ tp('detail.openingCg.loadVersion') }}</Button>
        <details v-if="frozen">
          <summary>{{ frozen.version.version }} · {{ frozen.version.package_hash.slice(0, 12) }}</summary>
          <pre class="pp__config" data-testid="novel-published-version-config">{{ JSON.stringify(frozen, null, 2) }}</pre>
        </details>
        <label :for="`cg-kind-${pack.id}`">{{ tp('detail.openingCg.assetKind') }}</label>
        <select :id="`cg-kind-${pack.id}`" v-model="cgKind" data-testid="novel-cg-kind">
          <option value="video">{{ tp('detail.openingCg.video') }}</option>
          <option value="poster">{{ tp('detail.openingCg.poster') }}</option>
          <option value="captions">{{ tp('detail.openingCg.captions') }}</option>
        </select>
        <label :for="`cg-file-${pack.id}`">{{ tp('detail.openingCg.file') }}</label>
        <input :id="`cg-file-${pack.id}`" :key="cgKind" type="file" :accept="{ video: '.mp4', poster: '.jpg', captions: '.vtt' }[cgKind]" data-testid="novel-cg-file" @change="cgFile = $event.target.files?.[0] ?? null" />
        <Button size="sm" :loading="cgBusy" :disabled="!cgFile || cgBusy" data-testid="novel-cg-upload" @click="uploadCg">{{ tp('detail.openingCg.upload') }}</Button>
        <p v-if="cgError" class="pp__risk" role="alert">{{ cgError.text }}</p>
        <a v-if="cgResult" class="pp__link pp__mono" :href="cgResult.public_url" target="_blank" rel="noopener" data-testid="novel-cg-upload-result">{{ cgResult.kind }} · {{ cgResult.sha256.slice(0, 12) }}</a>
      </section>

      <PackRelease v-if="pack.status !== 'deleted'" :pack="pack" :versions="detail.versions" @refreshed="detail = $event" />

      <section class="pp__section">
        <h4>{{ tp('detail.assets') }}</h4>
        <p v-if="detail.assets.length === 0" class="pp__muted">{{ tp('detail.noAssets') }}</p>
        <ul v-else class="pp__assets">
          <li v-for="asset in detail.assets" :key="asset.id">
            <img :src="asset.public_url" :alt="tp(`detail.assetKinds.${asset.kind}`)" loading="lazy" />
            <Badge size="sm">{{ tp(`detail.assetKinds.${asset.kind}`) }}</Badge>
          </li>
        </ul>
      </section>
    </div>
    <template #footer>
      <Button variant="ghost" @click="emit('close')">{{ tp('detail.close') }}</Button>
    </template>
  </Modal>
</template>

<style scoped>
.pp { display: grid; gap: var(--drama-space-lg); }
.pp__cover { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: cover; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); }
.pp__head { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: var(--drama-space-md); }
.pp__name { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.pp__name h3 { margin: 0; font-size: var(--drama-font-size-lg); overflow-wrap: anywhere; }
.pp__links { display: flex; flex-wrap: wrap; gap: var(--drama-space-md); }
.pp__link { display: inline-flex; align-items: center; gap: var(--drama-space-xs); color: var(--drama-color-primary); text-decoration: none; }
.pp__link:hover { text-decoration: underline; }
.pp__link:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; border-radius: var(--drama-radius-sm); }
.pp__risk { margin: 0; color: var(--drama-color-danger); }
.pp__section { display: grid; gap: var(--drama-space-sm); }
.pp__section h4 { margin: 0; font-size: var(--drama-font-size-md); }
.pp__text { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
.pp__muted { color: var(--drama-color-text-muted); }
.pp__mono { font-family: var(--drama-font-family-mono, monospace); }
.pp__cast { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--drama-space-md); margin: 0; padding: 0; list-style: none; }
.pp__person { display: flex; gap: var(--drama-space-sm); padding: var(--drama-space-sm); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); }
.pp__avatar { flex: none; display: grid; place-items: center; width: 48px; height: 48px; border-radius: var(--drama-radius-full); object-fit: cover; background: var(--drama-color-surface); color: var(--drama-color-text-muted); font-weight: var(--drama-font-weight-medium); }
.pp__person-body { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.pp__person-name { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-xs); margin: 0; font-weight: var(--drama-font-weight-medium); }
.pp__opening { display: grid; gap: var(--drama-space-sm); max-height: 360px; overflow-y: auto; padding: var(--drama-space-sm) var(--drama-space-md); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); }
.pp__speaker { display: block; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.pp__facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-sm) var(--drama-space-lg); margin: 0; }
.pp__facts div { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.pp__facts dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.pp__facts dd { margin: 0; overflow-wrap: anywhere; }
.pp__assets { display: flex; flex-wrap: wrap; gap: var(--drama-space-md); margin: 0; padding: 0; list-style: none; }
.pp__assets li { display: grid; gap: var(--drama-space-xs); justify-items: start; }
.pp__assets img { display: block; width: 96px; height: 96px; object-fit: cover; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); }
.pp__config { max-height: 300px; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; }
</style>
